import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import VoiceOnboarding from '../Onboarding/VoiceOnboarding';
import ProfileReview from '../Onboarding/ProfileReview';

const GENDER_OPTIONS = [
  'Man', 'Woman', 'Non-binary', 'Transgender', 'Genderqueer',
  'Genderfluid', 'Agender', 'Two-Spirit', 'Other',
];

const PRONOUN_OPTIONS = ['He/Him', 'She/Her', 'They/Them', 'Ze/Zir', 'Custom'];

const ORIENTATION_OPTIONS = [
  'Straight', 'Gay', 'Lesbian', 'Bisexual', 'Pansexual',
  'Asexual', 'Queer', 'Questioning', 'Other',
];

const INTERESTED_IN_OPTIONS = ['Men', 'Women', 'Non-binary people', 'Everyone'];

const STEPS = [
  'Account Info',
  'Identity',
  'Voice Onboarding',
  'Review Profile',
];

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    padding: '20px',
    background: 'var(--bg-primary)',
    overflow: 'hidden',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
    paddingTop: '20px',
    flexShrink: 0,
  },
  logo: {
    fontSize: '28px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, var(--accent), #f7a)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '16px',
  },
  progress: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'center',
    marginBottom: '8px',
  },
  progressDot: {
    width: '40px',
    height: '4px',
    borderRadius: '2px',
    background: 'var(--border-color)',
    transition: 'background 300ms',
  },
  progressDotActive: {
    background: 'var(--accent)',
  },
  stepTitle: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    margin: '0 auto',
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '32px 24px',
    border: '1px solid var(--border-color)',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  cardFull: {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  stepHeading: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  footer: {
    textAlign: 'center',
    padding: '16px',
    fontSize: '14px',
    color: 'var(--text-secondary)',
    flexShrink: 0,
  },
  link: {
    color: 'var(--accent)',
    textDecoration: 'none',
    fontWeight: '500',
  },
};

export default function Register() {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Step 0: Account info
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 1: Identity
  const [gender, setGender] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [orientation, setOrientation] = useState('');
  const [interestedIn, setInterestedIn] = useState([]);

  // Step 3: Profile from voice onboarding
  const [generatedProfile, setGeneratedProfile] = useState(null);

  const toggleInterestedIn = (value) => {
    setInterestedIn((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const validateStep = () => {
    setError('');
    switch (step) {
      case 0:
        if (!email || !password || !displayName) {
          setError('Please fill in all required fields');
          return false;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        if (dateOfBirth) {
          const age = Math.floor(
            (Date.now() - new Date(dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000)
          );
          if (age < 18) {
            setError('You must be at least 18 years old');
            return false;
          }
        }
        return true;
      case 1:
        if (!gender) {
          setError('Please select your gender identity');
          return false;
        }
        if (interestedIn.length === 0) {
          setError('Please select who you are interested in');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  // Register account + save identity, then proceed to voice onboarding
  const handleIdentitySubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError('');

    try {
      await register({
        email,
        password,
        display_name: displayName,
        date_of_birth: dateOfBirth || undefined,
      });

      await api.put('/users/profile', {
        gender,
        pronouns,
        orientation,
        interested_in: interestedIn,
      });

      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Voice onboarding complete — received generated profile
  const handleVoiceComplete = useCallback((profile) => {
    setGeneratedProfile(profile);
    setStep(3);
  }, []);

  // "Continue later" — save partial profile, skip to main app
  const handleContinueLater = useCallback(async () => {
    await refreshUser();
    navigate('/discover');
  }, [refreshUser, navigate]);

  // Profile review saved — navigate to discover
  const handleProfileSaved = useCallback(async () => {
    await refreshUser();
    navigate('/discover');
  }, [refreshUser, navigate]);

  // Go back to voice onboarding (redo conversation)
  const handleRedoConversation = useCallback(() => {
    setGeneratedProfile(null);
    setStep(2);
  }, []);

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <h2 style={styles.stepHeading}>Create your account</h2>
            <div style={styles.form}>
              <div className="form-group">
                <label className="form-label">Display name *</label>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="How others will see you"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    style={{ paddingRight: '48px' }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                    padding: 8, fontSize: 16, minWidth: 44, minHeight: 44,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {showPassword ? '\u{1F648}' : '\u{1F441}'}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Date of birth</label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]}
                />
              </div>
            </div>
          </>
        );

      case 1:
        return (
          <>
            <h2 style={styles.stepHeading}>About you</h2>
            <div style={styles.form}>
              <div className="form-group">
                <label className="form-label">Gender identity *</label>
                <div className="chip-group">
                  {GENDER_OPTIONS.map((g) => (
                    <button
                      key={g}
                      className={`chip ${gender === g ? 'active' : ''}`}
                      onClick={() => setGender(g)}
                      type="button"
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Pronouns</label>
                <div className="chip-group">
                  {PRONOUN_OPTIONS.map((p) => (
                    <button
                      key={p}
                      className={`chip ${pronouns === p ? 'active' : ''}`}
                      onClick={() => setPronouns(p)}
                      type="button"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Sexual orientation</label>
                <div className="chip-group">
                  {ORIENTATION_OPTIONS.map((o) => (
                    <button
                      key={o}
                      className={`chip ${orientation === o ? 'active' : ''}`}
                      onClick={() => setOrientation(o)}
                      type="button"
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Interested in *</label>
                <div className="chip-group">
                  {INTERESTED_IN_OPTIONS.map((i) => (
                    <button
                      key={i}
                      className={`chip ${interestedIn.includes(i) ? 'active' : ''}`}
                      onClick={() => toggleInterestedIn(i)}
                      type="button"
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        );

      case 2:
        return (
          <VoiceOnboarding
            onComplete={handleVoiceComplete}
            onPause={handleContinueLater}
            userName={displayName}
          />
        );

      case 3:
        return generatedProfile ? (
          <ProfileReview
            profile={generatedProfile}
            onSave={handleProfileSaved}
            onBack={handleRedoConversation}
          />
        ) : null;

      default:
        return null;
    }
  };

  const isFullScreenStep = step >= 2;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.logo}>nova</h1>
        <div style={styles.progress}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.progressDot,
                ...(i <= step ? styles.progressDotActive : {}),
              }}
            />
          ))}
        </div>
        <p style={styles.stepTitle}>
          Step {step + 1} of {STEPS.length}: {STEPS[step]}
        </p>
      </div>

      <div style={isFullScreenStep ? styles.cardFull : styles.card}>
        {renderStep()}
        {!isFullScreenStep && error && (
          <p className="error-text" style={{ marginTop: '12px' }}>{error}</p>
        )}

        {!isFullScreenStep && (
          <div style={styles.actions}>
            {step > 0 && (
              <button
                className="btn btn-secondary"
                onClick={prevStep}
                style={{ flex: 1 }}
              >
                Back
              </button>
            )}
            {step === 0 && (
              <button
                className="btn btn-primary"
                onClick={nextStep}
                style={{ flex: 1 }}
              >
                Next
              </button>
            )}
            {step === 1 && (
              <button
                className="btn btn-primary"
                onClick={handleIdentitySubmit}
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading ? 'Creating account...' : 'Next'}
              </button>
            )}
          </div>
        )}
      </div>

      <div style={styles.footer}>
        Already have an account?{' '}
        <Link to="/login" style={styles.link}>
          Sign in
        </Link>
      </div>
    </div>
  );
}
